/*
 * Copyright Evollis 2021
 * All Rights Reserved.
 *
 * Aplazame (APZ) checkout page: uses @aplazame/browser AplazameIFrame SDK for iframe (avoids 403).
 * Listens to status/state events, redirect on KO/OK; submit via apzIframe.request('submit').
 */
import Footer from "../../components/main/Footer"
import Basket from "../../components/organisms/Basket"
import { Header } from "../../components/organisms/Header"
import { Topbar } from "../../components/organisms/Topbar"
import { STEPS } from "../../constants"
import { sendToLogServer } from "../../core/service/BusinessLogicService"
import { initPdiComponent } from "../../core/service/BusinessLogicService"
import { loc } from "../../core/utils"
import { createCheckout, getAplazameConfig } from "../../service/apzApi"
import { TunnelDataContext } from "../context/TunnelDataContext"
import { AplazameIFrame } from "@aplazame/browser"
import React, { useCallback, useContext, useEffect, useRef, useState } from "react"
import { Button, Col, Container, Row, Spinner } from "react-bootstrap"
import { FiAlertCircle, FiRefreshCw } from "react-icons/fi"

/** Timeout (ms) after which we show an error if checkout/config is still not ready */
const INIT_TIMEOUT_MS = 30000

function logApzUiError(scope, err) {
  const detail = [err?.response?.status, err?.response?.data?.message || err?.response?.data?.error, err?.message]
    .filter(Boolean)
    .join(" | ")
  sendToLogServer("ERROR", `ApzCheckoutPage ${scope}: ${detail || "unknown"}`)
}

export default function ApzCheckoutPage({ tunnelData, history, onSetStep }) {
  const { setTunnelData } = useContext(TunnelDataContext)
  const { opl, apiUrl, access_token: accessToken, tenant } = tunnelData
  const [checkoutId, setCheckoutId] = useState(tunnelData.apzCheckoutId ?? null)
  const [publicKey, setPublicKey] = useState(tunnelData.apzPublicKey ?? null)
  const [iframeBaseUrl, setIframeBaseUrl] = useState(tunnelData.apzIframeBaseUrl ?? null)
  const [loading, setLoading] = useState(!tunnelData.apzCheckoutId)
  const [error, setError] = useState(null)
  const [iframeReady, setIframeReady] = useState(false)
  const [submitDisabled, setSubmitDisabled] = useState(true)
  const apzContainerRef = useRef(null)
  const apzIframeRef = useRef(null)
  const submitLockRef = useRef(false)
  const initTimeoutRef = useRef(null)
  /** Prevents duplicate createCheckout calls (e.g. React Strict Mode mount/unmount/remount) */
  const checkoutCreationInFlightRef = useRef(false)
  /** Empêche plusieurs redirections si Aplazame émet plusieurs fois « ok » */
  const netheosRedirectInFlightRef = useRef(false)
  /** Empêche plusieurs redirections si Aplazame émet plusieurs fois « ko » / « rejected » */
  const scoreKoRedirectInFlightRef = useRef(false)
  /** Refs to avoid iframe effect re-run when parent re-renders with new callback refs */
  const onSetStepRef = useRef(onSetStep)
  const historyRef = useRef(history)
  const setTunnelDataRef = useRef(setTunnelData)
  const tunnelDataRef = useRef(tunnelData)
  const logMilestoneRef = useRef(() => {})

  const transactionId = opl ?? tunnelData.data?.order?.id ?? null
  const logMilestone = useCallback(
    (step, detail = "") => {
      const msg = `APZ [${tenant}] opl=${transactionId} step=${step} ${detail}`.trim()
      sendToLogServer("INFO", msg)
    },
    [tenant, transactionId]
  )

  onSetStepRef.current = onSetStep
  historyRef.current = history
  setTunnelDataRef.current = setTunnelData
  tunnelDataRef.current = tunnelData
  logMilestoneRef.current = logMilestone

  // Une seule fois au montage : setTunnelData du provider est recréé à chaque rendu parent (inline),
  // ne pas le mettre en deps sinon boucle infinie + rafales getAplazameConfig / createCheckout.
  useEffect(() => {
    initPdiComponent("ApzCheckoutPage")
    const fn = setTunnelDataRef.current
    if (typeof fn === "function") {
      fn({ step: STEPS.APZ_CHECKOUT })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fetch config : deps stables uniquement (pas setTunnelData).
  useEffect(() => {
    if (!apiUrl || !accessToken) return
    if (iframeBaseUrl) return

    let cancelled = false
    getAplazameConfig(apiUrl, accessToken)
      .then((config) => {
        if (cancelled) return
        const url = config?.iframeBaseUrl
        if (!url || typeof url !== "string") {
          sendToLogServer("ERROR", "ApzCheckoutPage getAplazameConfig: missing iframeBaseUrl in response")
          setError(loc`Unable to load payment configuration. Please try again.`)
          return
        }
        setIframeBaseUrl(url)
        const persist = setTunnelDataRef.current
        if (typeof persist === "function") {
          persist({ apzIframeBaseUrl: url })
        }
      })
      .catch((err) => {
        if (cancelled) return
        logApzUiError("getAplazameConfig", err)
        setError(loc`Unable to load payment configuration. Please try again.`)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, accessToken, iframeBaseUrl])

  // Create checkout on mount if not already present (idempotent on backend).
  // Use only primitive deps to avoid effect re-runs when tunnelData reference changes (which would
  // cancel the in-flight promise and leave loading stuck true).
  /** opl prioritaire, sinon id commande */
  const apzTransactionId = tunnelData.data?.order?.id
  const tenantVal = tenant ?? undefined
  const correlationIdVal = tunnelData.correlationId ?? undefined

  /* setTunnelData (provider inline) et logMilestone instables : ne pas les mettre en deps */
  useEffect(() => {
    if (checkoutId || !apzTransactionId || !apiUrl || !accessToken) return
    if (checkoutCreationInFlightRef.current) return

    let cancelled = false
    checkoutCreationInFlightRef.current = true
    setLoading(true)
    setError(null)
    createCheckout(apzTransactionId, apiUrl, accessToken, {
      tenant: tenantVal,
      correlationId: correlationIdVal
    })
      .then((res) => {
        const cid = res?.checkoutId
        const pk = res?.publicKey ?? null
        if (!cid || typeof cid !== "string") {
          if (!cancelled) {
            sendToLogServer("ERROR", "ApzCheckoutPage createCheckout: missing checkoutId in response")
            setError(loc`Unable to start payment. Please try again.`)
          }
          return
        }
        // Apply result even when cancelled so the first (successful) response is used and we don't trigger a second call
        setCheckoutId(cid)
        setPublicKey(pk)
        const persist = setTunnelDataRef.current
        if (typeof persist === "function") {
          persist({
            apzCheckoutId: cid,
            apzPublicKey: pk
          })
        }
        if (!cancelled) {
          logMilestoneRef.current("checkout_created", `checkoutId=${cid.slice(0, 8)}...`)
        }
      })
      .catch((err) => {
        if (cancelled) return
        logApzUiError("createCheckout", err)
        logMilestoneRef.current("checkout_create_error", String(err?.message))
        setError(loc`Unable to start payment. Please try again.`)
      })
      .finally(() => {
        checkoutCreationInFlightRef.current = false
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [apzTransactionId, apiUrl, accessToken, checkoutId, tenantVal, correlationIdVal])

  // Build "ready to show iframe" from config (SDK uses url + searchParams, no raw iframe URL).
  const hasBaseUrl = iframeBaseUrl && typeof iframeBaseUrl === "string" && iframeBaseUrl.trim() !== ""
  const hasCheckoutId = checkoutId && typeof checkoutId === "string" && checkoutId.trim() !== ""
  const hasPublicKey = publicKey != null && String(publicKey).trim() !== ""
  const canShowIframe = hasBaseUrl && hasCheckoutId && hasPublicKey
  const waitingForConfig = (checkoutId && publicKey && !iframeBaseUrl) || (!checkoutId && !iframeBaseUrl && !error)
  const showLoading = !error && !canShowIframe && (loading || waitingForConfig)

  // Mount AplazameIFrame from @aplazame/browser (avoids 403 with raw iframe); register status/state handlers.
  useEffect(() => {
    if (!canShowIframe || !apzContainerRef.current) return

    // URL must end with / so that query string becomes /?param=... (required by Aplazame)
    const url = iframeBaseUrl.trim().endsWith("/") ? iframeBaseUrl.trim() : iframeBaseUrl.trim() + "/"
    const apzIframe = new AplazameIFrame({
      url,
      searchParams: {
        publicKey,
        checkoutId,
        Accept: "application/vnd.aplazame.v4+json"
      }
    })

    apzIframe
      .on("status", ({ details: statusDetail }) => {
        const name = statusDetail?.name ?? statusDetail?.status
        const onSetStepFn = onSetStepRef.current
        const currentTunnelData = tunnelDataRef.current
        sendToLogServer(
          "INFO",
          "Aplazame iframe : new status for transaction : " +
            currentTunnelData?.opl +
            " : " +
            JSON.stringify(statusDetail)
        )
        if (name === "waiting-document-validation" && typeof onSetStepFn === "function") {
          onSetStepFn()
          return
        }
        if (name === "ko" || name === "rejected") {
          if (scoreKoRedirectInFlightRef.current) {
            logMilestoneRef.current("score_ko_redirect_skipped_duplicate")
            return
          }
          scoreKoRedirectInFlightRef.current = true
          logMilestoneRef.current("status_ko")
          // Mutate the current state object so handleSetStep (sync read) sees scoreKO
          // even if setTunnelData has not flushed yet. Avoid mutating a stale closure.
          if (currentTunnelData) {
            currentTunnelData.scoreKO = true
          }
          if (typeof onSetStepFn === "function") {
            onSetStepFn()
          }
          // Fallback navigation (same pattern as OK → Netheos): guarantee leave checkout
          setTunnelDataRef.current({ scoreKO: true, step: STEPS.SCORE_KO })
          historyRef.current.push("/digital-journey/score-ko")
          return
        }
        if (name === "ok" || name === "approved" || name === "accepted") {
          const txId = apzTransactionId ?? transactionId
          if (!txId) {
            logMilestoneRef.current("complete_checkout_error", "missing_transaction_id")
            setTunnelDataRef.current({ step: STEPS.NETHEOS, apzCompleteCheckoutError: true })
            historyRef.current.push("/digital-journey/netheos")
            return
          }
          if (netheosRedirectInFlightRef.current) {
            logMilestoneRef.current("netheos_redirect_skipped_duplicate")
            return
          }
          netheosRedirectInFlightRef.current = true
          logMilestoneRef.current("status_ok")
          setSubmitDisabled(true)
          submitLockRef.current = true
          setTunnelDataRef.current({ step: STEPS.NETHEOS })
          historyRef.current.push("/digital-journey/netheos")
        }
      })
      .on("state", ({ details: stateDetail }) => {
        if (stateDetail == null) return
        const { hasError, isSubmitting, availableActions } = stateDetail
        setSubmitDisabled(!!hasError || !!isSubmitting || !availableActions?.includes?.("submit"))
      })

    apzIframeRef.current = apzIframe
    apzIframe.mount(apzContainerRef.current)
    apzIframe.setStyles({ width: "100%", height: "565px", border: "none" })
    setIframeReady(true)

    return () => {
      if (apzIframeRef.current) {
        apzIframeRef.current.unmount()
      }
      apzIframeRef.current = null
      setIframeReady(false)
    }
  }, [canShowIframe, iframeBaseUrl, publicKey, checkoutId, transactionId, apzTransactionId])

  const handleSubmit = () => {
    if (submitLockRef.current || submitDisabled) return
    try {
      if (apzIframeRef.current) {
        apzIframeRef.current.request("submit")
      }
    } catch (_) {
      setError(loc`Unable to submit payment. Please try again.`)
    }
  }

  // Timeout fallback: if we're still waiting for checkout/config after INIT_TIMEOUT_MS, show error
  useEffect(() => {
    if (canShowIframe || error) return
    initTimeoutRef.current = window.setTimeout(() => {
      setError(loc`Payment initialization is taking longer than expected. Please try again or contact support.`)
      setLoading(false)
    }, INIT_TIMEOUT_MS)
    return () => {
      if (initTimeoutRef.current) {
        window.clearTimeout(initTimeoutRef.current)
        initTimeoutRef.current = null
      }
    }
  }, [canShowIframe, error, checkoutId, publicKey, iframeBaseUrl])

  if (showLoading) {
    return (
      <Container fluid className="white-background">
        <Row noGutters>
          <Col className="main-block" lg={8}>
            <Row>
              <Topbar tunnelData={tunnelData} history={history} />
              <Header tunnelData={tunnelData} />
              <Col sm={12}>
                <Container>
                  <div className="text-center py-5">
                    <Spinner animation="border" />
                    <p className="mt-3">{loc`Initializing secure payment...`}</p>
                  </div>
                </Container>
              </Col>
              <Col sm={12}>
                <Container>
                  <Footer tunnelData={{ ...tunnelData, step: STEPS.SUMMARY }} />
                </Container>
              </Col>
            </Row>
          </Col>
          <Col className="aside-block d-none d-lg-block d-xl-block" lg={4}>
            <Basket tunnelData={tunnelData} />
          </Col>
        </Row>
      </Container>
    )
  }

  const handleRetry = () => {
    setError(null)
    setCheckoutId(null)
    setPublicKey(null)
    setIframeBaseUrl(null)
    setLoading(true)
  }

  if (error) {
    return (
      <Container fluid className="white-background">
        <Row noGutters>
          <Col className="main-block" lg={8}>
            <Row>
              <Topbar tunnelData={tunnelData} history={history} />
              <Header tunnelData={tunnelData} />
              <Col sm={12}>
                <Container>
                  <div className="apz-checkout-error" role="alert">
                    <div className="apz-checkout-error__icon" aria-hidden="true">
                      <FiAlertCircle size={24} />
                    </div>
                    <p className="apz-checkout-error__message">{error}</p>
                    <Button variant="primary" className="apz-checkout-error__retry" onClick={handleRetry}>
                      <FiRefreshCw size={18} aria-hidden="true" />
                      {loc`Retry`}
                    </Button>
                  </div>
                </Container>
              </Col>
              <Col sm={12}>
                <Container>
                  <Footer tunnelData={{ ...tunnelData, step: STEPS.SUMMARY }} />
                </Container>
              </Col>
            </Row>
          </Col>
          <Col className="aside-block d-none d-lg-block d-xl-block" lg={4}>
            <Basket tunnelData={tunnelData} />
          </Col>
        </Row>
      </Container>
    )
  }

  return (
    <Container fluid className="white-background">
      <Row noGutters>
        <Col className="main-block" lg={8}>
          <Row>
            <Topbar tunnelData={tunnelData} history={history} />
            <Header tunnelData={tunnelData} />
            <Col sm={12}>
              <Container>
                <p className="mb-3">{loc`Complete your secure payment below.`}</p>
                {canShowIframe && (
                  <div className="apz-checkout-content">
                    <div
                      ref={apzContainerRef}
                      className="apz-checkout-iframe-container"
                      aria-label="Aplazame checkout"
                    />
                    {iframeReady && (
                      <div className="apz-checkout-cta">
                        <Button
                          variant="success"
                          className="btn-solid btn"
                          disabled={submitDisabled}
                          onClick={handleSubmit}
                        >
                          {loc`Continue`}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </Container>
            </Col>
            <Col sm={12}>
              <Container>
                <Footer tunnelData={{ ...tunnelData, step: STEPS.SUMMARY }} />
              </Container>
            </Col>
          </Row>
        </Col>
        <Col className="aside-block d-none d-lg-block d-xl-block" lg={4}>
          <Basket tunnelData={tunnelData} />
        </Col>
      </Row>
    </Container>
  )
}
